import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  Divider,
  Avatar,
  LinearProgress,
  Chip,
  Alert,
  styled,
  useTheme,
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Palette as ThemeIcon,
  Language as LanguageIcon,
  Notifications as NotificationIcon,
  CreditCard as CreditCardIcon,
  BarChart as UsageIcon,
  Edit as EditIcon,
  PrivacyTip as PrivacyTipIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getPrivacyPolicyUrl, getUserPrivacyChoicesUrl, PRIVACY_POLICY_PATH } from '../../../constants/legalUrls';
import { collection, query, where, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { COLLECTIONS } from '../../../types/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../../../contexts/ThemeContext';
import { SubscriptionPlan } from '../../../constants/Subscriptions';
import { getSubscriptionLimits } from '../../../services/subscriptionService';
import PlanSwitchModal from '../modals/PlanSwitchModal';
import { usePlanSwitching } from '../../../hooks/usePlanSwitching';
import {
  createPortal,
  type CompanySubscriptionFromFirestore,
  type SubscriptionStatus,
  toUnixSeconds,
} from '../../../services/stripeWorkerClient';
import { getPortalBillingUrls } from '../../../constants/stripePrices';

function subscriptionStatusLabel(status: SubscriptionStatus): string {
  const labels: Record<SubscriptionStatus, string> = {
    trialing: 'Trialing',
    active: 'Active',
    past_due: 'Past due',
    canceled: 'Canceled',
    unpaid: 'Unpaid',
  };
  return labels[status] ?? status;
}

function getStripeBillingPresentation(
  sub: CompanySubscriptionFromFirestore | null,
  formatSubDate: (unix: number | null) => string
): {
  title: string;
  detail: string;
  chipColor: 'success' | 'warning' | 'error' | 'default' | 'info';
  accessActive: boolean;
} {
  if (!sub?.status) {
    return {
      title: 'No Stripe subscription',
      detail:
        'No billing subscription is linked to this company yet. Admins can complete checkout or change plan to activate.',
      chipColor: 'default',
      accessActive: false,
    };
  }

  const { status, cancelAtPeriodEnd, trialEnd, currentPeriodEnd } = sub;
  const periodEndLabel = formatSubDate(toUnixSeconds(currentPeriodEnd));

  if (status === 'canceled' || status === 'unpaid') {
    return {
      title: subscriptionStatusLabel(status),
      detail:
        status === 'canceled'
          ? `Subscription is canceled. Plan shown below may be your last plan. Period ended: ${periodEndLabel}.`
          : 'Payment could not be completed. An admin should open Manage subscription and update the payment method.',
      chipColor: 'error',
      accessActive: false,
    };
  }

  if (status === 'past_due') {
    return {
      title: 'Past due',
      detail:
        'Payment failed; Stripe may retry. An admin should update the payment method under Manage subscription.',
      chipColor: 'warning',
      accessActive: true,
    };
  }

  if (status === 'trialing') {
    return {
      title: 'Trialing',
      detail: `Trial ends ${formatSubDate(toUnixSeconds(trialEnd))}.`,
      chipColor: 'info',
      accessActive: true,
    };
  }

  if (status === 'active' && cancelAtPeriodEnd) {
    return {
      title: 'Canceling',
      detail: `Still active until ${periodEndLabel}. You can reactivate in the billing portal before then.`,
      chipColor: 'warning',
      accessActive: true,
    };
  }

  if (status === 'active') {
    return {
      title: 'Active',
      detail: `Renews ${periodEndLabel}.`,
      chipColor: 'success',
      accessActive: true,
    };
  }

  return {
    title: subscriptionStatusLabel(status),
    detail: `Stripe status: ${status}.`,
    chipColor: 'default',
    accessActive: false,
  };
}

const SettingsCard = styled(Card)(() => ({
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(34, 211, 238, 0.25)',
  borderTop: '1px solid rgba(34, 211, 238, 0.5)',
  borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
  borderRadius: '20px',
  marginBottom: '20px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
}));

const SectionTitle = styled(Typography)(() => ({
  fontFamily: "'JetBrains Mono', monospace",
  color: '#22d3ee',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  marginBottom: '15px',
  display: 'block',
}));

const SettingItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px 0',
}));

const SettingLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}));

const LogoutButton = styled(Button)(() => ({
  color: '#ffffff',
  border: '1px solid rgba(244, 67, 54, 0.5)',
  background: 'rgba(244, 67, 54, 0.15)',
  borderRadius: '50px',
  fontWeight: 'bold',
  padding: '10px 20px',
  '&:hover': {
    background: 'rgba(244, 67, 54, 0.3)',
    borderColor: 'rgba(244, 67, 54, 0.7)',
  },
}));

interface LocalSubscriptionPlan {
  name: 'Basic' | 'Premium' | 'Enterprise';
  price: number;
  features: string[];
  limits: {
    sites: number;
    users: number;
  };
  isUnlimited?: boolean;
}

interface UserPreferences {
  theme: 'light' | 'dark';
  language: string;
  timezone: string;
  emailNotifications: boolean;
}

const SettingsTab: React.FC = () => {
  const { userProfile, logout } = useAuth();
  const { isDarkMode, toggleTheme, colors } = useCustomTheme();
  const theme = useTheme();
  const [userCount, setUserCount] = useState<number>(0);
  const [siteCount, setSiteCount] = useState<number>(0);
  const [subscriptionLimits, setSubscriptionLimits] = useState<any>(null);
  const [planSwitchModalOpen, setPlanSwitchModalOpen] = useState(false);
  const [stripeSubscription, setStripeSubscription] = useState<CompanySubscriptionFromFirestore | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Use the plan switching hook
  const { currentPlan, loading: planLoading, loadCurrentPlan, switchPlan } = usePlanSwitching();

  // Mock data - replace with actual data later
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'light',
    language: 'en',
    timezone: 'America/New_York',
    emailNotifications: true,
  });

  // Fetch user count and site count from Firebase
   useEffect(() => {
     const fetchUserCount = async () => {
       if (!userProfile?.masterOwnerID) return;
       
       try {
         const usersQuery = query(
           collection(db, COLLECTIONS.USERS),
           where('masterOwnerID', '==', userProfile.masterOwnerID)
         );
         const usersSnapshot = await getDocs(usersQuery);
         setUserCount(usersSnapshot.size);
       } catch (error) {
         console.error('Error fetching user count:', error);
         setUserCount(1); // Fallback to 1
       }
     };

     const fetchSiteCount = async () => {
       if (!userProfile?.masterOwnerID) return;
       
       try {
         // Query the SITES collection same way as SitesTab
         const sitesQuery = query(
           collection(db, COLLECTIONS.SITES),
           where('masterOwnerID', '==', userProfile.masterOwnerID)
         );
         const sitesSnapshot = await getDocs(sitesQuery);
         
         // For Clerk users, filter by assigned sites
         if (userProfile?.role === 'Clerk' && userProfile.assignedSites) {
           // Count only assigned sites for Clerk users
           const assignedSiteCount = userProfile.assignedSites.length;
           setSiteCount(assignedSiteCount);
         } else {
           // For Admin and Tech users, count all sites
           setSiteCount(sitesSnapshot.size);
         }
       } catch (error) {
         console.error('Error fetching site count:', error);
         // Fallback: try to use assignedSites if available
         if (userProfile?.role === 'Clerk' && userProfile?.assignedSites) {
           setSiteCount(userProfile.assignedSites.length);
         } else {
           setSiteCount(0);
         }
       }
     };

    const fetchSubscriptionLimits = async () => {
      if (!userProfile?.masterOwnerID) return;
      
      try {
        const limits = await getSubscriptionLimits(userProfile.masterOwnerID);
        setSubscriptionLimits(limits);
      } catch (error) {
        console.error('Error fetching subscription limits:', error);
      }
    };

    fetchUserCount();
    fetchSiteCount();
    fetchSubscriptionLimits();
    loadCurrentPlan();
  }, [userProfile?.masterOwnerID, userProfile?.assignedSites, loadCurrentPlan]);

  useEffect(() => {
    if (!userProfile?.masterOwnerID) return;
    const ref = doc(db, COLLECTIONS.COMPANIES, userProfile.masterOwnerID);
    const unsub = onSnapshot(ref, (snap) => {
      const raw = snap.data()?.subscription as CompanySubscriptionFromFirestore | undefined;
      setStripeSubscription(raw ?? null);
    });
    return () => unsub();
  }, [userProfile?.masterOwnerID]);

  // Get current plan data for display using hook's currentPlan or fallback
  const getDisplayPlan = (): LocalSubscriptionPlan => {
    if (currentPlan) {
      return {
        name: currentPlan.title as any,
        price: currentPlan.price,
        features: currentPlan.features,
        limits: {
          sites: currentPlan.maxSites ?? (currentPlan.category === 'law-enforcement' ? 0 : 2),
          users: currentPlan.maxUsers ?? 5,
        },
        isUnlimited: currentPlan.id === 'Corp-Enterprise' || currentPlan.id === 'LEO-Enterprise',
      };
    }

    if (subscriptionLimits) {
      return {
        name: subscriptionLimits.planName || 'Basic',
        price: subscriptionLimits.planPrice || 0,
        features: subscriptionLimits.features || ['Basic features'],
        limits: {
          sites: subscriptionLimits.sites ?? subscriptionLimits.maxSites ?? 2,
          users: subscriptionLimits.users ?? subscriptionLimits.maxUsers ?? 5,
        },
        isUnlimited: subscriptionLimits.isUnlimited || false,
      };
    }

    // Fallback - default Corporate Basic Plan values
    return {
      name: 'Basic',
      price: 20,
      features: ['Basic features'],
      limits: { sites: 2, users: 5 },
      isUnlimited: false,
    };
  };

  const displayPlan = getDisplayPlan();

  // Usage data
  const usage = {
    sites: siteCount,
    users: userCount,
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  const handleNotificationToggle = () => {
    setPreferences(prev => ({
      ...prev,
      emailNotifications: !prev.emailNotifications,
    }));
  };

  const formatSubDate = (unix: number | null) => {
    if (unix == null) return '—';
    try {
      return new Date(unix * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const billingPresentation = getStripeBillingPresentation(stripeSubscription, formatSubDate);
  const showBillingStatusCard = stripeSubscription != null || userProfile?.role === 'Admin';

  const handleManageSubscription = async () => {
    if (!stripeSubscription?.stripeCustomerId) return;
    setPortalError(null);
    setPortalLoading(true);
    try {
      const { portalReturnUrl } = getPortalBillingUrls();
      const { portalUrl } = await createPortal(stripeSubscription.stripeCustomerId, portalReturnUrl);
      window.location.assign(portalUrl);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not open billing portal';
      setPortalError(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleOpenPlanSwitch = () => {
    setPlanSwitchModalOpen(true);
  };

  const handleClosePlanSwitch = () => {
    setPlanSwitchModalOpen(false);
  };

  const handlePlanSwitch = async (
    newPlan: SubscriptionPlan,
    isAnnual: boolean,
    options?: { enterpriseCode?: string }
  ) => {
    try {
      await switchPlan(newPlan, isAnnual, options);
      
      // Refresh subscription limits after successful plan switch
      if (userProfile?.masterOwnerID) {
        const limits = await getSubscriptionLimits(userProfile.masterOwnerID);
        setSubscriptionLimits(limits);
      }
    } catch (error) {
      console.error('Error switching plan:', error);
      throw error;
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <Box>
      {/* Account Settings */}
      <SettingsCard>
        <CardContent>
          <SectionTitle>Account Settings</SectionTitle>

          <SettingItem>
            <SettingLabel>
              <Avatar sx={{ bgcolor: colors.Gradient }}>
                {userProfile?.name?.[0] || userProfile?.email?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {userProfile?.name}
                </Typography>
                <Typography variant="body2" color={colors.TextSecondary}>
                  {userProfile?.email}
                </Typography>
                <Typography variant="caption" color={colors.TextSecondary}>
                  Role: {userProfile?.role}
                </Typography>
              </Box>
            </SettingLabel>
          </SettingItem>

          <Divider sx={{ my: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />

          {userProfile?.role === 'Admin' && (
            <>
          <SettingItem>
            <SettingLabel>
              <Typography>Edit Profile</Typography>
            </SettingLabel>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('editCompanyProfile'));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(34, 211, 238, 0.5)',
                background: 'rgba(34, 211, 238, 0.15)',
                borderRadius: '50px',
                fontWeight: 'bold',
                px: 2,
                '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              }}
            >
              Edit Profile
            </Button>
          </SettingItem>

              <Divider sx={{ my: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />
            </>
          )}

          <SettingItem>
            <SettingLabel>
              <Typography>Logout</Typography>
            </SettingLabel>
            <LogoutButton
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </LogoutButton>
          </SettingItem>
        </CardContent>
      </SettingsCard>

      {/* Billing & Subscription */}
      <SettingsCard>
        <CardContent>
          <SectionTitle>Billing & Subscription</SectionTitle>

          {userProfile?.role === 'Admin' && portalError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPortalError(null)}>
              {portalError}
            </Alert>
          )}

          {showBillingStatusCard && (
            <Box
              mb={2}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(34, 211, 238, 0.25)',
                backgroundColor: 'rgba(34, 211, 238, 0.06)',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Subscription billing
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={1}>
                <Chip size="small" color={billingPresentation.chipColor} label={billingPresentation.title} />
                {stripeSubscription?.status && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Stripe: ${subscriptionStatusLabel(stripeSubscription.status)}`}
                  />
                )}
                {stripeSubscription?.billingInterval && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Billed: ${stripeSubscription.billingInterval}`}
                  />
                )}
                {stripeSubscription && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={billingPresentation.accessActive ? 'Access: on' : 'Access: off'}
                    color={billingPresentation.accessActive ? 'success' : 'default'}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {billingPresentation.detail}
              </Typography>
            </Box>
          )}

          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                Current Plan: {displayPlan.name}
              </Typography>
              <Chip
                label={
                  stripeSubscription?.unitAmountCents != null && stripeSubscription.billingInterval
                    ? `$${(stripeSubscription.unitAmountCents / 100).toFixed(2)}/${
                        stripeSubscription.billingInterval === 'year' ? 'year' : 'month'
                      }`
                    : displayPlan.isUnlimited && displayPlan.price === 0
                    ? 'Custom'
                    : `$${displayPlan.price}/month`
                }
                sx={{
                  borderRadius: '50px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: '#22d3ee',
                  border: '1px solid rgba(34, 211, 238, 0.3)',
                  fontWeight: 'bold',
                  '& .MuiChip-label': { px: 1.5 },
                }}
              />
            </Box>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
              {displayPlan.features.map((feature: string, index: number) => (
                <Box key={index}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    ✓ {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />

          <SectionTitle>Usage Overview</SectionTitle>

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Sites</Typography>
              <Typography variant="body2">
                {usage.sites}/{displayPlan.limits.sites}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getUsagePercentage(usage.sites, displayPlan.limits.sites)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: colors.Background,
                '& .MuiLinearProgress-bar': {
                  bgcolor: colors.Gradient,
                },
              }}
            />
          </Box>

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Users</Typography>
              <Typography variant="body2">
                {usage.users}/{displayPlan.isUnlimited ? 'Custom' : displayPlan.limits.users}
              </Typography>
            </Box>
            {!displayPlan.isUnlimited && (
              <LinearProgress
                variant="determinate"
                value={getUsagePercentage(usage.users, displayPlan.limits.users)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: colors.Background,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: usage.users >= displayPlan.limits.users ? colors.Error || '#f44336' : colors.Gradient,
                  },
                }}
              />
            )}
            {displayPlan.isUnlimited && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                User limits are set for your organization—custom, not open-ended. All plan options follow your
                agreement.
              </Typography>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Need more resources? Change your plan to better fit your needs!
            </Typography>
          </Alert>

          <Box display="flex" gap={2} flexWrap="wrap">
            {userProfile?.role === 'Admin' && stripeSubscription?.stripeCustomerId && (
              <Button
                variant="outlined"
                onClick={handleManageSubscription}
                disabled={portalLoading || planLoading}
                startIcon={<CreditCardIcon />}
                sx={{
                  color: '#ffffff',
                  border: '1px solid rgba(34, 211, 238, 0.5)',
                  background: 'rgba(34, 211, 238, 0.15)',
                  borderRadius: '50px',
                  fontWeight: 'bold',
                  px: 2,
                  flex: 1,
                  minWidth: 160,
                  '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
                  '&:disabled': {
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.5)',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                {portalLoading ? 'Opening…' : 'Manage subscription'}
              </Button>
            )}
            {userProfile?.role === 'Admin' && (
              <Button
                variant="outlined"
                onClick={handleOpenPlanSwitch}
                disabled={planLoading}
                sx={{
                  color: '#ffffff',
                  border: '1px solid rgba(34, 211, 238, 0.5)',
                  background: 'rgba(34, 211, 238, 0.15)',
                  borderRadius: '50px',
                  fontWeight: 'bold',
                  px: 2,
                  flex: 1,
                  minWidth: 160,
                  '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
                  '&:disabled': {
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.5)',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                {planLoading ? 'Loading...' : 'Change Plan'}
              </Button>
            )}
          </Box>
        </CardContent>
      </SettingsCard>

      {/* Preferences */}
      <SettingsCard>
        <CardContent>
          <SectionTitle>Preferences</SectionTitle>

          <SettingItem>
            <SettingLabel>
              <Box>
                <Typography>Theme</Typography>
                <Typography variant="caption" color={colors.TextSecondary}>
                  Switch between light and dark mode
                </Typography>
              </Box>
            </SettingLabel>
            <Switch
              checked={isDarkMode}
              onChange={handleThemeToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: colors.Gradient,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: colors.Gradient,
                },
              }}
            />
          </SettingItem>

          <Divider sx={{ my: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />

          <SettingItem>
            <SettingLabel>
              <Box>
                <Typography>Language</Typography>
                <Typography variant="caption" color={colors.TextSecondary}>
                  Choose your preferred language
                </Typography>
              </Box>
            </SettingLabel>
            <Typography variant="body2" color={colors.TextSecondary}>
              English (Default)
            </Typography>
          </SettingItem>

          <Divider sx={{ my: 2, borderColor: 'rgba(34, 211, 238, 0.2)' }} />

          <SettingItem>
            <SettingLabel>
              <Box>
                <Typography>Email Notifications</Typography>
                <Typography variant="caption" color={colors.TextSecondary}>
                  Receive email notifications for reports
                </Typography>
              </Box>
            </SettingLabel>
            <Switch
              checked={preferences.emailNotifications}
              onChange={handleNotificationToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: colors.Gradient,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: colors.Gradient,
                },
              }}
            />
          </SettingItem>
        </CardContent>
      </SettingsCard>

      {/* Privacy — public URLs for App Store Connect */}
      <SettingsCard>
        <CardContent>
          <SectionTitle>Privacy</SectionTitle>
          <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 1.5 }}>
            Use these public links in App Store Connect (App Privacy) or your storefront listing. No sign-in required.
          </Typography>
          <Typography variant="caption" color={colors.TextSecondary} display="block" sx={{ mb: 0.5 }}>
            Privacy Policy URL
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              wordBreak: 'break-all',
              fontFamily: 'ui-monospace, monospace',
              color: colors.TextPrimary,
            }}
          >
            {getPrivacyPolicyUrl()}
          </Typography>
          <Typography variant="caption" color={colors.TextSecondary} display="block" sx={{ mb: 0.5 }}>
            User Privacy Choices URL (optional)
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              wordBreak: 'break-all',
              fontFamily: 'ui-monospace, monospace',
              color: colors.TextPrimary,
            }}
          >
            {getUserPrivacyChoicesUrl()}
          </Typography>
          <Button
            component={RouterLink}
            to={PRIVACY_POLICY_PATH}
            variant="outlined"
            startIcon={<PrivacyTipIcon />}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            }}
          >
            Open privacy policy
          </Button>
        </CardContent>
      </SettingsCard>

      {/* Save Preferences */}
      <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
        <Button
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
          }}
        >
          Save Preferences
        </Button>
      </Box>

      {/* Plan Switch Modal */}
      <PlanSwitchModal
        open={planSwitchModalOpen}
        onClose={handleClosePlanSwitch}
        currentPlan={currentPlan && currentPlan.id ? currentPlan : undefined}
        onPlanSwitch={handlePlanSwitch}
      />
    </Box>
  );
};

export default SettingsTab;
